import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Col, Button, Form, DatePicker } from 'antd';
import { useMutation } from '@common/hooks';
import moment from 'moment';
import {
  createMessage,
  mutationResponseMessage,
} from 'works/legacy/lib/message-helper';
import { UpdateArrivalDateMutation } from 'works/graphql/organization-profile';
import { US_DATE_FORMAT, toUSFormat } from 'works/legacy/lib/date-helper';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { Icon } from '@common/components';

function EditModal({
  organizationProfileId,
  estimatedArrivalDate,
  arrivedAtDate,
  visible,
  onClose,
}) {
  const [form] = Form.useForm();

  const initialValues = useMemo(() => {
    return {
      estimatedArrivalDate: estimatedArrivalDate
        ? moment(estimatedArrivalDate)
        : null,
      arrivedAtDate: arrivedAtDate ? moment(arrivedAtDate) : null,
    };
  }, [estimatedArrivalDate, arrivedAtDate]);

  const [editOrganizationProfile, { loading }] = useMutation(
    UpdateArrivalDateMutation
  );

  useEffect(() => {
    if (!visible) {
      form.setFieldsValue(initialValues);
    }
  }, [visible, form, initialValues]);

  const handleSubmit = (values) => {
    editOrganizationProfile({
      variables: {
        organizationProfileId: organizationProfileId,
        estimatedArrivalDate: values.estimatedArrivalDate,
        arrivedAtDate: values.arrivedAtDate,
      },
    }).then((response) => {
      const errors = response.data.updateArrivalDates.errors;
      if (errors) {
        addErrorsToForm(form, errors);
      } else {
        mutationResponseMessage({
          gqlResponse: response,
          onSuccess: () => {
            createMessage('success', 'Works details has been updated!');
            onClose();
          },
          errorMessage:
            'Failed to update estimated arrival date. Please try again.',
        });
      }
    });
  };

  if (!visible) return null;

  return (
    <>
      <Modal
        title="Edit Worker"
        visible={visible}
        onCancel={() => onClose()}
        okText="Update Candidate Details"
        confirmLoading={true}
        className="EditArrivalDateModal"
        footer={[
          <Button key="back" onClick={onClose}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            htmlType="submit"
            onClick={form.submit}
            loading={loading}
          >
            Update Details
          </Button>,
        ]}
      >
        <Form
          onFinish={handleSubmit}
          initialValues={initialValues}
          form={form}
          layout="vertical"
        >
          <Form.Item label="Estimated Arrival" name="estimatedArrivalDate">
            <DatePicker
              placeholder="mm/dd/yyyy"
              format={US_DATE_FORMAT}
              style={{ width: '100%' }}
              disabled={loading}
            />
          </Form.Item>

          <Form.Item label="Confirmed Arrival" name="arrivedAtDate">
            <DatePicker
              placeholder="mm/dd/yyyy"
              format={US_DATE_FORMAT}
              style={{ width: '100%' }}
              disabled={loading}
            />
          </Form.Item>
        </Form>
      </Modal>
      <style jsx>{`
        :global(.EditArrivalDateModal .ant-modal-footer) {
          text-align: right;
        }
      `}</style>
    </>
  );
}

EditModal.propTypes = {
  organizationProfileId: PropTypes.string.isRequired,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  estimatedArrivalDate: PropTypes.string,
  arrivedAtDate: PropTypes.string,
};

function EditArrivalDateSupplier({
  arrivedAtDate,
  estimatedArrivalDate,
  organizationProfileId,
  canUpdateArrivalDates,
}) {
  const [showModal, setShowModal] = useState(false);

  const formattedEstimatedArrivalDate = estimatedArrivalDate
    ? toUSFormat(estimatedArrivalDate)
    : null;
  const formattedArrivedAtDate = arrivedAtDate
    ? toUSFormat(arrivedAtDate)
    : null;
  const openEditModalOnClick = (children) => (
    <a onClick={() => setShowModal(true)}>{children}</a>
  );

  const hasArrived = moment(arrivedAtDate).isBefore();

  return (
    <>
      <EditModal
        arrivedAtDate={arrivedAtDate}
        estimatedArrivalDate={estimatedArrivalDate}
        visible={showModal}
        onClose={() => setShowModal(false)}
        organizationProfileId={organizationProfileId}
      />

      {formattedEstimatedArrivalDate && !formattedArrivedAtDate && (
        <Col>
          <Icon
            icon="planeArrival"
            kind="light"
            size="sm"
            tooltipText="Arrived At"
          />
          {'Est. Arrival: '}
          {canUpdateArrivalDates
            ? openEditModalOnClick(formattedEstimatedArrivalDate)
            : formattedEstimatedArrivalDate}
        </Col>
      )}

      {!formattedEstimatedArrivalDate &&
        !formattedArrivedAtDate &&
        canUpdateArrivalDates && (
          <Col>
            <Icon
              icon="planeArrival"
              kind="light"
              size="sm"
              tooltipText="Estimated Arrival Date"
            />
            {openEditModalOnClick('Est. Arrival TBD')}
          </Col>
        )}
      {formattedArrivedAtDate && (
        <Col>
          <Icon
            icon="planeArrival"
            kind="light"
            size="sm"
            tooltipText="Arrived At"
          />
          {hasArrived ? 'Arrived: ' : 'Arrival Date: '}
          {canUpdateArrivalDates
            ? openEditModalOnClick(formattedArrivedAtDate)
            : formattedArrivedAtDate}
        </Col>
      )}
    </>
  );
}

EditArrivalDateSupplier.propTypes = {
  organizationProfileId: PropTypes.string.isRequired,
  canUpdateArrivalDates: PropTypes.bool.isRequired,
  estimatedArrivalDate: PropTypes.string,
  arrivedAtDate: PropTypes.string,
};

export { EditArrivalDateSupplier };
